import r from '../Utils/fetch';

// Client de l'API admin du service api-agents : organisation d'agents IA.
// URL de base injectée au runtime (window.API_AGENTS_URL), token Firebase
// ajouté par le wrapper fetch (Utils/fetch).
//
// Trois niveaux, trois propriétaires :
//   1. l'organisation (rôles, liens, cadence, applicabilité) : du code, lecture seule ;
//   2. la définition d'un agent (mission, consignes, actions par défaut) : éditable ici,
//      versionnée, « revenir au code » possible ;
//   3. la configuration d'un projet (activé, contexte, actions) : le client via le MCP,
//      l'admin « pour son compte ».

const w: any = window;
const API_URL = w.API_AGENTS_URL;
const base = () => `${API_URL}/v1/admin`;

// =============================================================================
// TYPES — alignés sur apps/api-agents/app/org
// =============================================================================

export type Severity = 'info' | 'warning' | 'critical';
export type RoleKind = 'specialist' | 'manager';
export type DefinitionSource = 'code' | 'admin';

export interface AgentAction {
	id: string;
	name: string;
	description: string;
	params: Record<string, unknown>;
	severity: Severity;
	default_enabled: boolean;
}

export type RequirementKind = 'family' | 'connector';
export type RequirementLevel = 'required' | 'recommended';

/** Prérequis d'un agent : `values` = « l'un de ». Requis = familles seulement. */
export interface Requirement {
	kind: RequirementKind;
	values: string[];
	level: RequirementLevel;
	why: string;
}

export interface AgentDefinition {
	mission: string;
	planning_hints: string;
	actions: AgentAction[];
	requirements: Requirement[];
}

export interface CatalogConnector {
	sku: string;
	name: string;
	family: string;
}

export interface Catalog {
	connectors: CatalogConnector[];
	families: { value: string; label: string }[];
}

export interface Role {
	slug: string;
	name: string;
	parent: string;
	kind: RoleKind;
	schedule: 'daily' | 'weekly' | 'monthly';
	internal: boolean;
	applicability: string[];
	defaults: AgentDefinition;
}

export interface RoleView {
	role: Role;
	definition: AgentDefinition;
	source: DefinitionSource;
	version: number;
}

export interface DefinitionVersion {
	id: string;
	role: string;
	version: number;
	mission: string;
	planning_hints: string;
	actions: AgentAction[];
	updated_by: string;
	created_at: string;
}

export interface DefinitionView extends RoleView {
	code_defaults: AgentDefinition;
	versions: DefinitionVersion[];
}

export interface ActionState extends AgentAction {
	enabled: boolean;
	params: Record<string, unknown>; // valeurs effectives (défaut ⊕ client)
	default_params: Record<string, unknown>; // valeurs de la définition
	overridden: boolean;
}

export interface ProjectAgentView {
	role: Role;
	mission: string;
	source: DefinitionSource;
	applicable: boolean;
	reason?: string;
	enabled: boolean;
	context: string;
	actions: ActionState[];
	replan_requested_at?: string;
	updated_at?: string;
	updated_by?: string;
}

export interface ProjectView {
	project_folder_id: string;
	capabilities: {
		families: Record<string, number>;
		managed_dw: boolean;
		exists: boolean;
	};
	agents: ProjectAgentView[];
}

export interface ActionOverride {
	id: string;
	enabled?: boolean;
	params?: Record<string, unknown>;
}

export interface SettingsPatch {
	enabled?: boolean;
	context?: string;
	actions?: ActionOverride[];
}

// =============================================================================
// ORGANISATION & DÉFINITIONS
// =============================================================================

export const getOrg = () =>
	r<{ roles: RoleView[] }>({ url: `${base()}/org`, method: 'GET' });

export const getDefinition = (role: string) =>
	r<DefinitionView>({
		url: `${base()}/definitions/${encodeURIComponent(role)}`,
		method: 'GET',
	});

export const saveDefinition = (role: string, def: AgentDefinition) =>
	r<RoleView>({
		url: `${base()}/definitions/${encodeURIComponent(role)}`,
		method: 'PUT',
		data: def,
	});

/** Connecteurs publiés et familles, pour choisir un prérequis. */
export const getCatalog = () =>
	r<Catalog>({ url: `${base()}/catalog`, method: 'GET' });

export const resetDefinition = (role: string) =>
	r<{ ok: boolean }>({
		url: `${base()}/definitions/${encodeURIComponent(role)}`,
		method: 'DELETE',
	});

// =============================================================================
// PROJET
// =============================================================================

export const getProjectAgents = (projectFolderId: string) =>
	r<ProjectView>({
		url: `${base()}/projects/${encodeURIComponent(projectFolderId)}/agents`,
		method: 'GET',
	});

export const updateProjectAgent = (
	projectFolderId: string,
	role: string,
	patch: SettingsPatch
) =>
	r<ProjectAgentView>({
		url: `${base()}/projects/${encodeURIComponent(projectFolderId)}/agents/${encodeURIComponent(role)}`,
		method: 'PUT',
		data: patch,
	});

// #region costs
export interface AgentCostRow {
	key: string;
	runs: number;
	plans: number;
	syntheses: number;
	errors: number;
	signaled: number;
	llm_input_tokens: number;
	llm_output_tokens: number;
	bytes_processed: number;
	bytes_billed: number;
	llm_cache_write_tokens: number;
	llm_cache_read_tokens: number;
	duration_ms: number;
	count: number;
	/** Estimations en dollars (tarifs de `pricing`) */
	llm_usd: number;
	bq_usd: number;
	total_usd: number;
	avg_usd: number;
}
export interface AgentCostReport {
	from: string;
	to: string;
	group_by: string;
	pricing: {
		input_usd_per_mtok: number;
		output_usd_per_mtok: number;
		bq_usd_per_tib: number;
	};
	rows: AgentCostRow[];
	total: AgentCostRow;
}

/** Consommation des agents (30 derniers jours par défaut), groupée par rôle, projet ou kind. */
export const getCosts = (
	groupBy: 'role' | 'project' | 'kind' = 'role',
	from?: string,
	to?: string
) => {
	const q = new URLSearchParams({ groupBy });
	if (from) q.set('from', from);
	if (to) q.set('to', to);
	return r<AgentCostReport>({
		url: `${base()}/costs?${q.toString()}`,
		method: 'GET',
	});
};
// #endregion

// =============================================================================
// PLAFOND MENSUEL DES AGENTS (par projet)
// =============================================================================

export interface AgentBudget {
	month: string;
	limit_usd: number;
	spent_usd: number;
	percent: number;
	source: 'default' | 'project';
	exceeded: boolean;
	resets_at: string;
	default_usd: number;
}

const budgetUrl = (projectFolderId: string) =>
	`${base()}/projects/${encodeURIComponent(projectFolderId)}/budget`;

/** Consommation LLM estimée du mois et plafond du projet. */
export const getProjectBudget = (projectFolderId: string) =>
	r<AgentBudget>({ url: budgetUrl(projectFolderId), method: 'GET' });

/** monthlyUsd = null : retour au plafond par défaut de la plateforme. */
export const setProjectBudget = (
	projectFolderId: string,
	monthlyUsd: number | null
) =>
	r<AgentBudget>({
		url: budgetUrl(projectFolderId),
		method: 'PUT',
		data: { monthly_usd: monthlyUsd },
	});
